import { useMemo, useState } from 'react';
import type {
  ApiClient, HeizlastLaufDetail, HeizlastErgebnisRaum, HeizlastRaumBilanz,
  HeizlastErgebnisBauteil,
} from '../api';
import {
  IconEbene, IconRaum, IconSuche, IconWarnung, IconLueftung, IconHeizung,
} from '../Icons';

// =============================================================================
//  Beschriftungen
// =============================================================================
//
// Die Datenbank fuehrt Kleinschreibung ohne Umlaute - 'wand', 'innentuer',
// 'beheizter_nachbarraum'. Das ist als Schluessel richtig und als Anzeige
// falsch. Die Uebersetzung steht an EINER Stelle, damit Baum, Tabelle und
// spaeterer Ausdruck dieselben Woerter benutzen.

const RANDBEDINGUNG: Record<string, string> = {
  aussenluft: 'Außenluft',
  erdreich: 'Erdreich',
  unbeheizt: 'Unbeheizte Zone',
  unbeheizte_zone: 'Unbeheizte Zone',
  beheizter_nachbarraum: 'Beheizter Nachbarraum',
  adiabat: 'Adiabat',
  unbekannt: 'Unbekannt',
};

/**
 * Die Bauteilbezeichnung haengt an der Randbedingung, nicht nur an der Art.
 *
 * Dieselbe 'wand' ist eine Aussenwand, eine Innenwand oder eine
 * Kelleraussenwand - je nachdem, wogegen sie steht. Wer in der Liste zwanzig
 * Mal "Wand" liest, sieht den Unterschied nicht, und der Unterschied ist der
 * ganze Punkt: Die eine traegt 33,5 K, die andere null.
 */
export function bauteilName(art: string | null, rand: string | null): string {
  const a = (art ?? '').toLowerCase();
  const r = (rand ?? '').toLowerCase();

  const aussen = r === 'aussenluft';
  const erde = r === 'erdreich';
  const unbeheizt = r === 'unbeheizt' || r === 'unbeheizte_zone';
  const innen = r === 'beheizter_nachbarraum' || r === 'adiabat';

  switch (a) {
    case 'wand':
      if (aussen) return 'Außenwand';
      if (erde) return 'Kelleraußenwand';
      if (unbeheizt) return 'Wand zu unbeheizt';
      if (innen) return 'Innenwand';
      return 'Wand';

    case 'geschossdecke':
      if (aussen) return 'Decke gegen Außenluft';
      if (erde) return 'Bodenplatte';
      return 'Geschossdecke';

    case 'decke':
      return aussen ? 'Decke gegen Außenluft' : 'Decke';

    case 'boden':
      return erde ? 'Bodenplatte' : 'Boden';

    case 'dach': return 'Dach';
    case 'fenster': return 'Fenster';
    case 'oberlicht': return 'Oberlicht';
    case 'vorhangfassade': return 'Vorhangfassade';
    case 'aussentuer': return 'Außentür';
    case 'innentuer': return 'Innentür';
    case 'tuer': return aussen ? 'Außentür' : 'Innentür';
    case 'tor': return 'Tor';
    default:
      return art ? art.charAt(0).toUpperCase() + art.slice(1) : '—';
  }
}

/**
 * Der Revit-Typname, lesbar gemacht.
 *
 * Aus 'H_AWA_STB_FT_300mm' wird 'H AWA STB FT 300mm'. Mehr nicht - die
 * Kuerzel bleiben stehen, weil das Bauteil im Modell so heisst und ein Planer
 * es wiederfinden muss. Nur die Unterstriche gehen, die den Namen zu einer
 * einzigen langen Zeichenkette machen.
 */
function typLesbar(typ: string | null): string {
  if (!typ) return '';
  return typ.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
}

const HERKUNFT: Record<string, { text: string; marke: string }> = {
  nachweis: { text: 'Nachweis', marke: 'ok' },
  derived: { text: 'Schichten', marke: 'neutral' },
  modell: { text: 'Modell', marke: 'neutral' },
  typ: { text: 'Typ', marke: 'neutral' },
  ersatz: { text: 'Ersatzwert', marke: 'warnung' },
  ersatzwert: { text: 'Ersatzwert', marke: 'warnung' },
  hand: { text: 'von Hand', marke: 'ok' },
};

const zahl = (n: number | null | undefined, z = 0) =>
  n === null || n === undefined ? '—' : n.toLocaleString('de-DE', {
    minimumFractionDigits: z, maximumFractionDigits: z });

// =============================================================================
//  Die Seite
// =============================================================================

export function Raumergebnisse({ api, detail }: {
  api: ApiClient;
  detail: HeizlastLaufDetail;
}) {
  const [suche, setSuche] = useState('');
  const [offeneEbenen, setOffeneEbenen] = useState<Set<string>>(new Set());
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [bilanz, setBilanz] = useState<HeizlastRaumBilanz | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const ebenen = useMemo(() => {
    const s = suche.trim().toLowerCase();

    const passt = (r: HeizlastErgebnisRaum) => s === '' || [
      r.raumnummer, r.raumname, r.ebene, r.nutzung, r.zone,
    ].some((f) => f?.toLowerCase().includes(s));

    const nach = new Map<string, HeizlastErgebnisRaum[]>();

    for (const r of detail.raeume) {
      if (!passt(r)) continue;
      const e = r.ebene ?? 'Ohne Ebene';
      const liste = nach.get(e);
      if (liste) liste.push(r); else nach.set(e, [r]);
    }

    return [...nach.entries()]
      .map(([name, raeume]) => ({
        name,
        raeume: [...raeume].sort((a, b) => b.normheizlastW - a.normheizlastW),
        summeW: raeume.reduce((s2, r) => s2 + r.normheizlastW, 0),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [detail.raeume, suche]);

  const raum = detail.raeume.find((r) => r.id === gewaehlt) ?? null;

  const waehlen = async (id: string) => {
    setGewaehlt(id);
    setBilanz(null);
    setFehler(null);
    setLaedt(true);
    try {
      setBilanz(await api.heizlastLaufRaum(detail.lauf.id, id));
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setLaedt(false);
    }
  };

  const gesamt = ebenen.reduce((s, e) => s + e.raeume.length, 0);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr',
                  gap: 18, alignItems: 'start' }}>
      <Navigation
        ebenen={ebenen} gesamt={gesamt}
        offen={offeneEbenen}
        umschalten={(e) => {
          const neu = new Set(offeneEbenen);
          if (neu.has(e)) neu.delete(e); else neu.add(e);
          setOffeneEbenen(neu);
        }}
        gewaehlt={gewaehlt} waehlen={(id) => void waehlen(id)}
        suche={suche} setSuche={setSuche}
      />

      {raum
        ? <Raumdetail key={raum.id} raum={raum} bilanz={bilanz}
                      laedt={laedt} fehler={fehler} />
        : <div className="karte">
            <div className="leer">
              Links ein Geschoss aufklappen und einen Raum wählen.
            </div>
          </div>}
    </div>
  );
}

// --- Navigation --------------------------------------------------------------

function Navigation({
  ebenen, offen, umschalten, gewaehlt, waehlen, suche, setSuche, gesamt,
}: {
  ebenen: { name: string; raeume: HeizlastErgebnisRaum[]; summeW: number }[];
  offen: Set<string>;
  umschalten: (ebene: string) => void;
  gewaehlt: string | null;
  waehlen: (id: string) => void;
  suche: string;
  setSuche: (s: string) => void;
  gesamt: number;
}) {
  return (
    <div className="karte" style={{ padding: 0, position: 'sticky', top: 16 }}>
      <div style={{ padding: '14px 14px 10px' }}>
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute', left: 9, top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-sehr-schwach)', pointerEvents: 'none',
          }}>
            <IconSuche size={15} />
          </span>
          <input
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Nummer, Name oder Nutzung …"
            style={{ width: '100%', paddingLeft: 30 }}
          />
        </div>
        <p className="sehr-schwach" style={{ margin: '6px 0 0' }}>
          {gesamt} Räume
        </p>
      </div>

      <div style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto',
                    borderTop: '1px solid var(--linie)' }}>
        {ebenen.map((e) => (
          <div key={e.name}>
            <button
              onClick={() => umschalten(e.name)}
              style={{
                width: '100%', textAlign: 'left', border: 'none',
                borderBottom: '1px solid #f0f0f0', borderRadius: 0,
                padding: '9px 14px', fontWeight: 600, fontSize: 13,
                background: 'var(--flaeche)',
              }}
            >
              <span className="baum-ebene" style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                width: '100%',
              }}>
                <span style={{ width: 10, color: 'var(--text-sehr-schwach)',
                               fontSize: 11 }}>
                  {offen.has(e.name) ? '▾' : '▸'}
                </span>
                <IconEbene size={15} />
                {e.name}
                <span className="sehr-schwach" style={{ fontWeight: 400 }}>
                  ({e.raeume.length})
                </span>
                <span className="sehr-schwach" style={{
                  marginLeft: 'auto', fontWeight: 400 }}>
                  {zahl(e.summeW / 1000, 1)} kW
                </span>
              </span>
            </button>

            {offen.has(e.name) && e.raeume.map((r) => (
              <button
                key={r.id}
                onClick={() => waehlen(r.id)}
                title={r.raumname ?? undefined}
                style={{
                  width: '100%', textAlign: 'left', border: 'none',
                  borderBottom: '1px solid #f7f7f7', borderRadius: 0,
                  padding: '7px 14px 7px 30px', fontSize: 12,
                  background: gewaehlt === r.id ? '#e8f1f8' : 'transparent',
                  fontWeight: gewaehlt === r.id ? 600 : 400,
                  whiteSpace: 'nowrap', overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <span className="baum-raum" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  width: '100%' }}>
                  <IconRaum size={13} />
                  <strong>{r.raumnummer ?? r.zone ?? '—'}</strong>
                  <span className="sehr-schwach" style={{
                    overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.raumname}
                  </span>
                  {(r.vollstaendigkeitProzent ?? 100) < 100 && (
                    <IconWarnung size={12} color="var(--warnung)"
                                 title="Bauteile ohne U-Wert" />
                  )}
                  <span className="sehr-schwach" style={{
                    marginLeft: 'auto', fontWeight: 400 }}>
                    {zahl(r.normheizlastW / 1000, 1)}
                  </span>
                </span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Raumdetail --------------------------------------------------------------

export function Raumdetail({ raum, bilanz, laedt, fehler }: {
  raum: HeizlastErgebnisRaum;
  bilanz: HeizlastRaumBilanz | null;
  laedt: boolean;
  fehler: string | null;
}) {
  // Die vier Anteile in fester Reihenfolge und mit fester Farbe. Fest, damit
  // ein Betrachter sie zwischen zwei Raeumen wiedererkennt.
  // Die Gliederung einer Ausgabe nach EN 12831-1: Φ_T, Φ_V, Φ_RH.
  // ═══ Die Normvariante NEBEN dem heutigen Wert (Migration 0488) ═══════
  //
  // Drei Posten, die EN 12831-1 verlangt und die heute nicht gerechnet
  // werden (Befunde H-02, H-03 und H-16). Sie stehen als zweite SPALTE
  // neben dem Ergebnis und nicht an seiner Stelle: Welcher Wert gilt, ist
  // eine Festlegung des Fachplaners, und keiner der Ansätze ist bestätigt.
  //
  // `null` heißt „nicht gerechnet" — dann fällt die Spalte weg. Eine Spalte
  // mit lauter Strichen läse sich wie „gerechnet und kein Unterschied".
  const nachNorm = raum.vergleichNormheizlastW;

  const anteile = [
    { bez: 'Transmission Φ_T', w: raum.transmissionW, farbe: 'var(--akzent)',
      // Der Erdreichanteil wird ERSETZT und nicht addiert. Wer ihn addierte,
      // zählte den Boden zweimal.
      norm: raum.vergleichErdreichW === null ? null
        : raum.transmissionW - raum.erdreichW + raum.vergleichErdreichW },
    { bez: 'Wärmebrücken', w: raum.waermebrueckeW, farbe: '#7aa7c7',
      norm: null },
    { bez: 'Infiltration Φ_V,inf', w: raum.infiltrationW, farbe: '#b9b9b9',
      norm: null },
    // TL3 (Befund F-A3): Mindestluft und Raumlast der Zuluft gehören zu Φ_HL
    // und standen hier nicht - bei der Bowl fehlten 20 %. Die Summe der
    // Zeilen ist jetzt Φ_HL. Die Vergleichsvariante legt ihren Mindestluft-
    // strom auf diese Zeile, wie bisher auf die Lüftung.
    { bez: 'Mindestluftwechsel Φ_V,min', w: raum.mindestluftW ?? 0,
      farbe: 'var(--zustand-info)',
      norm: raum.vergleichMindestluftW === null ? null
        : (raum.mindestluftW ?? 0) + raum.vergleichMindestluftW },
    { bez: 'Raumlast der Zuluft Φ_V,Raum (θ_Zuluft → θ_int)',
      w: raum.raumlueftungW ?? 0, farbe: 'var(--zustand-neutral)', norm: null },
    { bez: 'Lufterhitzer Φ_Erh (θ nach WRG → θ_Zuluft)', w: raum.lueftungW,
      farbe: 'var(--warnung)', norm: null },
    { bez: 'Aufheizleistung Φ_RH', w: raum.aufheizleistungW ?? 0,
      farbe: '#c25e5e', norm: raum.vergleichAufheizW },
  ];

  // HUL-03: die Raumheizlast nach DIN EN 12831-1 ohne Lufterhitzer -
  // Grundlage der Heizflächen. Φ_HL bleibt die Zahl mit Erhitzer.
  const raumOhneErhitzer = raum.normheizlastW - raum.lueftungW;

  const summe = anteile.reduce((s, a) => s + a.w, 0);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {/* --- Kopf mit den Kennzahlen ------------------------------------- */}
      <div className="karte" style={{ position: 'sticky', top: 16, zIndex: 2 }}>
        <div className="bereich-kopf">
          <div>
            <h2 className="karte-titel" style={{ margin: 0 }}>
              {raum.raumnummer ?? raum.zone ?? '—'}{' '}
              <span className="schwach" style={{ fontWeight: 400 }}>
                {raum.raumname}
              </span>
            </h2>
            <p className="sehr-schwach" style={{ margin: '2px 0 0' }}>
              {[raum.ebene, raum.nutzung, raum.zone]
                .filter(Boolean).join(' · ') || '—'}
            </p>
          </div>
          {raum.istZone && (
            <span className="marke warnung">Zone mit Ersatzkörper</span>
          )}
        </div>

        <div className="kennzahlen" style={{ marginTop: 16 }}>
          <div>
            <div className="kennzahl-wert">
              {zahl(raum.normheizlastW / 1000, 2)}
            </div>
            <div className="kennzahl-name">kW Norm-Heizlast</div>
          </div>
          {nachNorm !== null && (
            <div>
              <div className="kennzahl-wert" style={{ color: 'var(--warnung)' }}
                   title="Vergleichsrechnung mit üblichen Werten — KEIN
                          Ergebnis dieses Laufs">
                {zahl(nachNorm / 1000, 2)}
              </div>
              <div className="kennzahl-name">
                kW nach Norm (übliche Werte)
              </div>
            </div>
          )}
          <div>
            <div className="kennzahl-wert">{zahl(raum.spezifischWm2, 0)}</div>
            <div className="kennzahl-name">W/m²</div>
          </div>
          <div>
            <div className="kennzahl-wert">{zahl(raum.flaecheM2, 1)}</div>
            <div className="kennzahl-name">m² Fläche</div>
          </div>
          <div>
            <div className="kennzahl-wert">{zahl(raum.volumenM3, 0)}</div>
            <div className="kennzahl-name">m³ Volumen</div>
          </div>
          <div>
            <div className="kennzahl-wert">
              {zahl(raum.innentemperaturC, 1)}
            </div>
            <div className="kennzahl-name">°C innen</div>
          </div>
          <div>
            <div className="kennzahl-wert">{zahl(raum.spezifischWm3, 2)}</div>
            <div className="kennzahl-name">W/m³</div>
          </div>
          <div>
            <div className="kennzahl-wert">
              {zahl((raum.htWk ?? 0) + (raum.hvWk ?? 0), 1)}
            </div>
            <div className="kennzahl-name">W/K H<sub>T</sub>+H<sub>V</sub></div>
          </div>
          <div>
            <div className="kennzahl-wert"
                 style={{ color: (raum.vollstaendigkeitProzent ?? 100) < 100
                   ? 'var(--warnung)' : undefined }}>
              {zahl(raum.vollstaendigkeitProzent, 0)}
            </div>
            <div className="kennzahl-name">% Vollständigkeit</div>
          </div>
        </div>
      </div>

      {raum.vorbehalt && (
        <div className="karte">
          <div className="hinweis warnung">
            <span className="hinweis-zeichen">!</span>
            <div>
              <strong>Vorbehalt</strong>
              <div style={{ marginTop: 2 }}>{raum.vorbehalt}</div>
            </div>
          </div>
        </div>
      )}

      {raum.warnungen && (
        <div className="karte">
          <h3 className="karte-titel">Hinweise zu diesem Raum</h3>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {raum.warnungen.split('; ').map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* --- Aufteilung ---------------------------------------------------- */}
      <div className="karte">
        <h3 className="karte-titel">
          Aufteilung der Heizlast
          {nachNorm !== null && (
            <span className="marke warnung" style={{ marginLeft: 8 }}>
              mit Vergleich nach Norm
            </span>
          )}
        </h3>

        {summe > 0 && (
          <div style={{ display: 'flex', height: 10, borderRadius: 5,
                        overflow: 'hidden', margin: '12px 0 14px',
                        background: 'var(--linie)' }}>
            {anteile.filter((a) => a.w > 0).map((a) => (
              <div key={a.bez}
                   title={`${a.bez}: ${zahl(a.w / 1000, 2)} kW`}
                   style={{ width: `${(a.w / summe) * 100}%`,
                            background: a.farbe }} />
            ))}
          </div>
        )}

        <table className="liste">
          {nachNorm !== null && (
            <thead>
              <tr>
                <th colSpan={2}></th>
                <th style={{ textAlign: 'right' }}>heute</th>
                <th style={{ textAlign: 'right', width: 70 }}>Anteil</th>
                <th style={{ textAlign: 'right' }}>
                  nach Norm (übliche Werte)
                </th>
                <th style={{ textAlign: 'right' }}>Differenz</th>
              </tr>
            </thead>
          )}
          <tbody>
            {anteile.map((a) => (
              <tr key={a.bez}>
                <td style={{ width: 22 }}>
                  <span style={{ display: 'inline-block', width: 10,
                                 height: 10, borderRadius: 2,
                                 background: a.farbe }} />
                </td>
                <td>{a.bez}</td>
                <td style={{ textAlign: 'right' }}>
                  <strong>{zahl(a.w / 1000, 2)} kW</strong>
                </td>
                <td style={{ textAlign: 'right', width: 70 }}
                    className="sehr-schwach">
                  {summe > 0 ? `${zahl((a.w / summe) * 100, 0)} %` : '—'}
                </td>

                {/* Die Normvariante. Ein Strich heißt hier „unverändert" und
                    nicht „unbekannt": Wärmebrücken und Infiltration rechnet
                    die Vergleichsvariante gar nicht anders. */}
                {nachNorm !== null && (
                  <>
                    <td style={{ textAlign: 'right' }}>
                      {a.norm === null
                        ? <span className="sehr-schwach">unverändert</span>
                        : <strong>{zahl(a.norm / 1000, 2)} kW</strong>}
                    </td>
                    <td style={{ textAlign: 'right' }}
                        className={a.norm === null ? 'sehr-schwach' : undefined}>
                      {a.norm === null ? '—'
                        : `${a.norm - a.w >= 0 ? '+' : ''}`
                          + `${zahl((a.norm - a.w) / 1000, 2)} kW`}
                    </td>
                  </>
                )}
              </tr>
            ))}

            {nachNorm !== null && (
              <tr style={{ borderTop: '1px solid var(--linie)' }}>
                <td></td>
                <td><strong>Norm-Heizlast Φ_HL</strong></td>
                <td style={{ textAlign: 'right' }}>
                  <strong>{zahl(raum.normheizlastW / 1000, 2)} kW</strong>
                </td>
                <td></td>
                <td style={{ textAlign: 'right' }}>
                  <strong>{zahl(nachNorm / 1000, 2)} kW</strong>
                </td>
                <td style={{ textAlign: 'right', color: 'var(--warnung)' }}>
                  <strong>
                    {nachNorm - raum.normheizlastW >= 0 ? '+' : ''}
                    {zahl((nachNorm - raum.normheizlastW) / 1000, 2)} kW
                  </strong>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <p className="klein" data-test="raum-ohne-erhitzer">
          Raumheizlast ohne Lufterhitzer Φ_HL,Raum (DIN EN 12831-1, Grundlage
          der Heizflächen): <strong>{zahl(raumOhneErhitzer / 1000, 2)} kW</strong>
          {' '}= Φ_HL {zahl(raum.normheizlastW / 1000, 2)} kW − Φ_Erh
          {' '}{zahl(raum.lueftungW / 1000, 2)} kW.
        </p>

        {nachNorm !== null && (
          <p className="sehr-schwach" style={{ marginBottom: 0 }}>
            Die Spalte „nach Norm" ist eine <strong>Vergleichsrechnung</strong>
            {' '}und kein Ergebnis: Wiederaufheizzuschlag nach EN 12831-1
            Abschnitt 6.5, hygienischer Mindestluftwechsel nach Abschnitt
            6.3.3 und Erdreichpfad mit U<sub>equiv</sub> nach EN ISO 13370 —
            alle drei mit <strong>unbestätigten üblichen Werten</strong>. Die
            Festlegung trifft der Fachplaner.
            {raum.vergleichMindestluftM3h !== null
              && ` Mindestluftstrom dieses Raums: `
                 + `${zahl(raum.vergleichMindestluftM3h, 0)} m³/h.`}
            {raum.vergleichHinweis && ` ${raum.vergleichHinweis}`}
          </p>
        )}
      </div>

      {laedt && <div className="karte"><div className="leer">lädt …</div></div>}

      {fehler && (
        <div className="karte">
          <div className="hinweis fehler">
            <span className="hinweis-zeichen">!</span><div>{fehler}</div>
          </div>
        </div>
      )}

      {bilanz && bilanz.raum.id === raum.id && (
        <>
          <Lueftungsblatt raum={raum} bilanz={bilanz} />
          <Bauteilblatt bauteile={bilanz.bauteile} />
        </>
      )}
    </div>
  );
}

// --- Lüftung -----------------------------------------------------------------

function Lueftungsblatt({ raum, bilanz }: {
  raum: HeizlastErgebnisRaum;
  bilanz: HeizlastRaumBilanz;
}) {
  if (bilanz.lueftung.length === 0 && !raum.aussenluftM3h) return null;

  const gekappt = raum.wrgGradWirksam !== null && raum.wrgGradNenn !== null
    && raum.wrgGradWirksam < raum.wrgGradNenn - 0.001;

  return (
    <div className="karte">
      <div className="bereich-kopf">
        <h3 className="karte-titel" style={{ margin: 0,
              display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <IconLueftung size={16} /> Lüftung
        </h3>
        <span className="marke neutral">
          {zahl(raum.aussenluftM3h, 0)} m³/h Außenluft
        </span>
      </div>

      {bilanz.lueftung.length === 0 ? (
        <p className="schwach" style={{ marginTop: 8 }}>
          Luftmenge liegt vor, aber keine Lüftungszeile — Zulufttemperatur und
          WRG-Grad sind Ersatzwerte.
        </p>
      ) : (
        <table className="liste" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Anlage</th>
              <th>Herkunft</th>
              <th style={{ textAlign: 'right' }}>Außenluft</th>
              <th style={{ textAlign: 'right' }}>Zuluft-Soll</th>
              <th style={{ textAlign: 'right' }}>WRG Nenn</th>
              <th style={{ textAlign: 'right' }}>WRG wirksam</th>
            </tr>
          </thead>
          <tbody>
            {bilanz.lueftung.map((l) => (
              <tr key={l.id}>
                <td>{l.anlage ?? (
                  <span className="sehr-schwach">ohne Anlagenbezug</span>)}</td>
                <td className="sehr-schwach">{l.herkunft ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  {zahl(l.aussenluftM3h ?? l.zuluftM3h, 0)} m³/h
                </td>
                <td style={{ textAlign: 'right' }}>
                  {zahl(l.zulufttemperaturHeizenC, 1)} °C
                </td>
                <td style={{ textAlign: 'right' }} className="sehr-schwach">
                  {zahl(l.wrgGradProzent, 1)} %
                </td>
                <td style={{ textAlign: 'right' }}>
                  {raum.wrgGradWirksam === null
                    ? '—' : `${zahl(raum.wrgGradWirksam * 100, 1)} %`}
                  {raum.wrgErsatzwert && (
                    <span className="marke warnung" style={{ marginLeft: 6 }}
                          title="Kein Anlagenbezug — Projektwert">
                      Ersatz
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {gekappt && (
        <p className="sehr-schwach" style={{ marginTop: 8 }}>
          Der wirksame Grad liegt unter dem Nenngrad: Der Frostschutz begrenzt
          die Rückgewinnung bei dieser Außentemperatur.
        </p>
      )}
    </div>
  );
}

// --- Bauteile ----------------------------------------------------------------

function Bauteilblatt({ bauteile }: { bauteile: HeizlastErgebnisBauteil[] }) {
  const offen = bauteile.filter((b) => !b.istGerechnet);

  // Nach Bauteilart zusammengefasst - die erste Frage bei jeder Heizlast ist,
  // woher sie kommt, nicht welches einzelne Bauteil wie viel traegt.
  const gruppen = useMemo(() => {
    const m = new Map<string, { w: number; m2: number; n: number }>();

    for (const b of bauteile) {
      if (!b.istGerechnet) continue;
      const k = bauteilName(b.bauteilart, b.randbedingung);
      const e = m.get(k) ?? { w: 0, m2: 0, n: 0 };
      e.w += b.transmissionW + b.waermebrueckeW;
      e.m2 += b.flaecheM2 ?? 0;
      e.n += 1;
      m.set(k, e);
    }

    return [...m.entries()].sort((a, b) => b[1].w - a[1].w);
  }, [bauteile]);

  return (
    <div className="karte">
      <div className="bereich-kopf">
        <h3 className="karte-titel" style={{ margin: 0,
              display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <IconHeizung size={16} /> Bauteile
        </h3>
        <span className={offen.length > 0 ? 'marke warnung' : 'marke neutral'}>
          {bauteile.length} Stück
          {offen.length > 0 && `, ${offen.length} nicht gerechnet`}
        </span>
      </div>

      {bauteile.length === 0 ? (
        <div className="leer">
          Keine Bauteilzuordnung — dieser Raum hat keine berechenbare Hülle.
        </div>
      ) : (
        <>
          {gruppen.length > 0 && (
            <table className="liste" style={{ marginTop: 8, marginBottom: 18 }}>
              <tbody>
                {gruppen.map(([name, g]) => (
                  <tr key={name}>
                    <td><strong>{name}</strong></td>
                    <td className="sehr-schwach">{g.n} ×</td>
                    <td style={{ textAlign: 'right' }} className="schwach">
                      {zahl(g.m2, 1)} m²
                    </td>
                    <td style={{ textAlign: 'right', width: 90 }}>
                      {zahl(g.w, 0)} W
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <table className="liste">
            <thead>
              {/* Die Spalten folgen der üblichen Ausgabe nach EN 12831-1.
                  H_T ist die temperaturfreie Kennzahl: Zwei Räume mit
                  verschiedenen Solltemperaturen lassen sich darüber
                  vergleichen, ihre Φ_T nicht. */}
              <tr>
                <th>Kz</th>
                <th>HR</th>
                <th>Bauteil</th>
                <th>Randbedingung</th>
                <th style={{ textAlign: 'right' }}>A m²</th>
                <th style={{ textAlign: 'right' }}>A' m²</th>
                <th style={{ textAlign: 'right' }}>U</th>
                <th style={{ textAlign: 'right' }}>ΔU<sub>wb</sub></th>
                <th style={{ textAlign: 'right' }}>θ<sub>bez</sub></th>
                <th style={{ textAlign: 'right' }}>ΔT</th>
                <th style={{ textAlign: 'right' }}>f<sub>x</sub></th>
                <th style={{ textAlign: 'right' }}>H<sub>T</sub></th>
                <th style={{ textAlign: 'right' }}>Φ<sub>T</sub> W</th>
              </tr>
            </thead>
            <tbody>
              {bauteile.map((b) => {
                const h = HERKUNFT[(b.uWertHerkunft ?? '').toLowerCase()];

                return (
                  <tr key={b.id}
                      className={!b.istGerechnet ? 'bauteil-offen' : undefined}>
                    <td><strong>{b.kuerzel ?? '—'}</strong></td>
                    <td className="sehr-schwach">
                      {b.himmelsrichtung ?? '—'}
                    </td>
                    <td>
                      <div>{bauteilName(b.bauteilart, b.randbedingung)}</div>
                      {b.typ && (
                        <div className="sehr-schwach" title={b.typ}
                             style={{ fontSize: 11 }}>
                          {typLesbar(b.typ)}
                        </div>
                      )}
                    </td>
                    <td className="schwach">
                      {RANDBEDINGUNG[(b.randbedingung ?? '').toLowerCase()]
                        ?? b.randbedingung ?? '—'}
                    </td>
                    <td style={{ textAlign: 'right' }} className="sehr-schwach">
                      {zahl(b.bruttoflaecheM2 ?? b.flaecheM2, 2)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {zahl(b.flaecheM2, 2)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {zahl(b.uWertWmk, 3)}
                      {h && (
                        <div>
                          <span className={`marke ${h.marke}`}
                                style={{ fontSize: 10 }}>{h.text}</span>
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }} className="sehr-schwach">
                      {zahl(b.waermebrueckeWm2k, 3)}
                    </td>
                    <td style={{ textAlign: 'right' }} className="sehr-schwach">
                      {zahl(b.bezugstemperaturC, 1)}
                    </td>
                    <td style={{ textAlign: 'right' }} className="sehr-schwach">
                      {zahl(b.deltaTK, 1)}
                    </td>
                    <td style={{ textAlign: 'right' }} className="sehr-schwach">
                      {zahl(b.korrekturfaktorB, 2)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {zahl(b.htWk, 2)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {b.istGerechnet
                        ? <strong>
                            {zahl(b.transmissionW + b.waermebrueckeW, 0)}
                          </strong>
                        : <span className="marke warnung"
                                title={b.hinweis ?? undefined}>
                            n. ger.
                          </span>}
                    </td>
                  </tr>
                );
              })}
              {/* Die Summenzeile muss mit Φ_T des Raums übereinstimmen —
                  wer nachrechnet, soll das sehen können. */}
              <tr style={{ background: 'var(--baugruppe)' }}>
                <td colSpan={11}><strong>Summe Transmission</strong></td>
                <td style={{ textAlign: 'right' }}>
                  <strong>
                    {zahl(bauteile.reduce((s, b) => s + (b.htWk ?? 0), 0), 2)}
                  </strong>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <strong>
                    {zahl(bauteile.reduce(
                      (s, b) => s + b.transmissionW + b.waermebrueckeW, 0), 0)}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
